import { call, put, select, takeLatest, fork, all } from 'redux-saga/effects';
import axios from 'axios';
import { SagaIterator } from 'redux-saga';

import { updateQueryString } from 'services/router/actions';
// @ts-ignore
import { idTokenSelector } from 'services/auth/selectors';
import { ERROR_MODAL_QUERY_STRING_PARAM } from 'components/ErrorModal/constants';
import {
    setSubmitHasFailed,
    deleteItems
} from 'services/virtualizedTable/actions';

import {
    RequestedCreateEquipmentSetAction,
    RequestedUpdateEquipmentSetAction,
    RequestDeleteEquipmentSetAction
} from './types';
import {
    succeededCreateEquipmentSet,
    failedCreateEquipmentSet,
    succeededUpdateEquipmentSet,
    failedUpdateEquipmentSet,
    setEquipmentSet,
    succeededDeleteEquipmentSet,
    failedDeleteEquipmentSet
} from './actions';
import {
    EQUIPMENT_SET_CREATE_REQUESTED,
    EQUIPMENT_SET_UPDATE_REQUESTED,
    EQUIPMENT_SET_DELETE_REQUESTED
} from './constants';

// Environment
const { REACT_APP_API_URL } = process.env;

export function* postEquipmentSet(
    action: RequestedCreateEquipmentSetAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);
    const url = `${REACT_APP_API_URL}/v1/SprayEquipmentSets`;

    try {
        const response = yield call(axios, url, {
            method: 'post',
            headers: {
                Authorization: idToken
            },
            data: action.payload
        });
        yield all([
            put(succeededCreateEquipmentSet(response)),
            put(setEquipmentSet(response.data.id, response.data))
        ]);
    } catch (error) {
        yield all([
            put(failedCreateEquipmentSet(error)),
            put(setSubmitHasFailed(true)),
            put(deleteItems([action.payload.uuid])),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: 'EQUIPMENT_SET_CREATE'
                })
            )
        ]);
    }
}

export function* watchCreateEquipmentSet(): SagaIterator {
    yield takeLatest(EQUIPMENT_SET_CREATE_REQUESTED, postEquipmentSet);
}

export function* patchEquipmentSet(
    action: RequestedUpdateEquipmentSetAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);

    const {
        payload: { equipmentSetId, equipmentSet }
    } = action;

    try {
        const url = `${REACT_APP_API_URL}/v1/SprayEquipmentSets/${equipmentSetId}`;

        const response = yield call(axios, url, {
            method: 'patch',
            headers: {
                Authorization: idToken
            },
            data: equipmentSet
        });
        yield put(succeededUpdateEquipmentSet(response));
        yield put(setEquipmentSet(response.data.id, response.data));
    } catch (error) {
        yield put(failedUpdateEquipmentSet(error));
        yield put(setSubmitHasFailed(true));
        yield put(
            updateQueryString({
                [ERROR_MODAL_QUERY_STRING_PARAM]: 'EQUIPMENT_SET_UPDATE'
            })
        );
    }
}

export function* deleteEquipmentSet(
    action: RequestDeleteEquipmentSetAction
): SagaIterator {
    const idToken = yield select(idTokenSelector);

    const {
        payload: { equipmentSetId }
    } = action;

    try {
        const url = `${REACT_APP_API_URL}/v1/SprayEquipmentSets/${equipmentSetId}`;

        yield call(axios, url, {
            method: 'delete',
            headers: {
                Authorization: idToken
            }
        });

        yield put(succeededDeleteEquipmentSet(equipmentSetId));
    } catch (error) {
        yield all([
            put(failedDeleteEquipmentSet(error)),
            put(setSubmitHasFailed(true)),
            put(
                updateQueryString({
                    [ERROR_MODAL_QUERY_STRING_PARAM]: 'EQUIPMENT_SET_DELETE'
                })
            )
        ]);
    }
}

export function* watchEquipmentSetUpdateRequested(): SagaIterator {
    yield takeLatest(EQUIPMENT_SET_UPDATE_REQUESTED, patchEquipmentSet);
}

export function* watchEquipmentSetDeleteRequested(): SagaIterator {
    yield takeLatest(EQUIPMENT_SET_DELETE_REQUESTED, deleteEquipmentSet);
}

export default function* equipmentSetsSagas(): SagaIterator {
    yield all([
        fork(watchCreateEquipmentSet),
        fork(watchEquipmentSetUpdateRequested),
        fork(watchEquipmentSetDeleteRequested)
    ]);
}
